import { useTranslation } from "react-i18next";
import { FileText, Pencil } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { cn } from "../../lib/utils.js";
import {
  compactTemplatePreviewText,
  templateLanguageDisplayName,
} from "../inbox/template-picker.js";
import { extractBodyPreview } from "../inbox/template-send.js";
import { templateContentDir } from "../whatsapp/WhatsAppTemplatePreview/template-content-dir.js";
import {
  campaignTemplateCategoryI18nKey,
  campaignTemplateStatusI18nKey,
} from "./campaign-message-presentation.js";

export function CampaignTemplateMetaChips({
  language,
  category,
  status,
}: {
  language: string | null | undefined;
  category: string | null | undefined;
  status: string | null | undefined;
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const chips: Array<{ key: string; label: string; tone: "language" | "category" | "success" }> = [];
  if (language) {
    chips.push({
      key: "language",
      label: templateLanguageDisplayName(language, i18n.language),
      tone: "language",
    });
  }
  if (category) {
    chips.push({
      key: "category",
      label: t(campaignTemplateCategoryI18nKey(category), { defaultValue: category }),
      tone: "category",
    });
  }
  if (status) {
    const approved = status.trim().toUpperCase() === "APPROVED";
    chips.push({
      key: "status",
      label: t(campaignTemplateStatusI18nKey(status), { defaultValue: status }),
      tone: approved ? "success" : "language",
    });
  }
  if (chips.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-wrap gap-1 p-0">
      {chips.map((chip) => (
        <li
          key={chip.key}
          className={cn(
            "inline-flex max-w-full items-center rounded-full border px-1.5 py-0.5 text-[0.65rem] font-medium leading-tight",
            chip.tone === "success" && "border-success/25 bg-success-muted text-success",
            chip.tone === "category" && "border-accent/20 bg-accent/8 text-foreground",
            chip.tone === "language" && "border-line/70 bg-surface-muted/70 text-muted",
          )}
        >
          {chip.label}
        </li>
      ))}
    </ul>
  );
}

export function CampaignTemplateCard({
  name,
  language,
  category,
  status,
  components,
  canMutate,
  unsupported,
  onChoose,
}: {
  name: string | null;
  language: string | null;
  category: string | null;
  status: string | null;
  components: unknown;
  canMutate: boolean;
  unsupported: boolean;
  onChoose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const selected = name != null && name.length > 0;
  const body = compactTemplatePreviewText(extractBodyPreview(components), 96);
  const contentDir = templateContentDir(language);

  return (
    <section className="grid gap-2 py-3" data-testid="campaign-template-card">
      <div className="flex items-start justify-between gap-2">
        <h2 className="m-0 flex min-w-0 items-center gap-2 text-[0.8125rem] font-semibold">
          <span
            className="inline-grid size-7 shrink-0 place-items-center rounded-md border border-line/70 bg-surface-muted text-muted"
            aria-hidden="true"
          >
            <FileText size={14} />
          </span>
          {t("campaigns.message.template")}
        </h2>
        {canMutate ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0"
            onClick={onChoose}
            data-testid="campaign-choose-template"
          >
            {selected ? <Pencil size={12} aria-hidden="true" /> : null}
            {selected ? t("campaigns.message.changeTemplate") : t("campaigns.message.chooseTemplate")}
          </Button>
        ) : null}
      </div>

      {selected ? (
        <div className="grid min-w-0 gap-1.5">
          <p className="m-0 truncate text-[0.875rem] font-semibold tracking-[-0.01em]">{name}</p>
          <CampaignTemplateMetaChips language={language} category={category} status={status} />
          {body ? (
            <p
              className="m-0 line-clamp-2 text-[0.75rem] leading-snug text-muted"
              dir={contentDir}
            >
              {body}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="m-0 text-[0.8125rem] text-muted">{t("campaigns.message.noTemplateSelected")}</p>
      )}

      {unsupported ? (
        <p
          className="m-0 rounded-md border border-warning/30 bg-warning-muted px-2.5 py-2 text-[0.75rem] text-warning"
          role="alert"
          data-testid="campaign-template-unsupported"
        >
          {t("campaigns.message.templateUnsupported")}
        </p>
      ) : null}
    </section>
  );
}
